/**
 * The Quality tab: per-check aggregates for this company, the failing ones
 * first, each expandable to individual readings (§10.3).
 *
 * This is where the `total_equals_sum_of_children` cluster becomes
 * inspectable instead of a number. Whether it is an extraction problem or an
 * over-strict check is exactly the question this app exists to let someone
 * answer, so the check that dominates the review queue is named at the top
 * rather than left to be noticed.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from 'wouter'
import type { CompanyQualityResponse } from '@server/sql/notes.ts'
import { ApiError, api, keys } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import { count, money, percent, percent0 } from '~/i18n/format.ts'
import { Badge, Bar, Label, Panel, Skeleton, bandOf } from '~/components/ui/primitives.tsx'
import { Disclosure } from '~/components/ui/disclosure.tsx'
import { EmptyState, ErrorState } from '~/components/ui/empty.tsx'
import { Untranslated } from '~/components/ui/raw.tsx'
import { Table, Td, Th, Tr } from '~/components/ui/table.tsx'
import { cn } from '~/lib/cn.ts'

export function CompanyQuality({ symbol }: { symbol: string }) {
  const { t, tn, locale, v } = useT()
  const { data, error, refetch } = useQuery({
    queryKey: keys.companyQuality(symbol),
    queryFn: () => api<CompanyQualityResponse>(`/api/companies/${encodeURIComponent(symbol)}/quality`),
  })

  if (error) {
    const e = error as ApiError
    return <ErrorState title={t('common.error')} detail={e.message} requestId={e.requestId}
      onRetry={() => void refetch()} retryLabel={t('action.retry')} />
  }
  if (!data) return <Skeleton className="h-96 w-full" />
  if (data.checks.length === 0) return <EmptyState title={t('empty.generic')} />

  const passRate = data.totals.checks > 0 ? data.totals.passed / data.totals.checks : null

  return (
    <div className="space-y-4">
      {/* --- the headline, always with its denominator ---------------- */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div>
          <Label>{t('quality.passRate')}</Label>
          <div className="mt-0.5 flex items-center gap-2">
            <Bar value={passRate} />
            <span
              className={cn(
                'tnum text-[length:var(--text-title)] font-medium',
                bandOf(passRate) === 'pass' ? 'text-[var(--pass)]'
                  : bandOf(passRate) === 'warn' ? 'text-[var(--warn)]' : 'text-[var(--fail)]',
              )}
            >
              {t('quality.passRateOf', {
                pct: percent(passRate, locale),
                passed: count(data.totals.passed, locale),
                total: count(data.totals.checks, locale),
              })}
            </span>
          </div>
        </div>
        <div>
          <Label>{t('docs.review')}</Label>
          <div className="tnum mt-0.5 text-[length:var(--text-title)]">
            {count(data.totals.openReview, locale)}
          </div>
        </div>
        <div>
          <Label>{t('quality.xbrl')}</Label>
          <div className="tnum mt-0.5 text-[length:var(--text-title)]">
            {data.totals.xbrlAgreement === null
              ? <span className="text-[var(--text-faint)]" title={t('quality.xbrlNone')}>—</span>
              : percent(data.totals.xbrlAgreement, locale)}
          </div>
        </div>
      </div>

      {data.dominant && (
        <p className="max-w-[92ch] rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--bg-sunken)] px-2.5 py-2 text-[length:var(--text-tiny)] leading-[var(--text-tiny--line-height)] text-[var(--text-muted)]">
          {t('quality.dominant', {
            pct: percent0(data.dominant.share, locale),
            check: v('checkName', data.dominant.name) ?? data.dominant.name,
          })}
        </p>
      )}

      {/* --- per check ------------------------------------------------- */}
      <div className="space-y-2">
        {data.checks.map((c) => {
          const rate = c.total > 0 ? c.passed / c.total : null
          return (
            <Panel key={c.name} className="px-3 py-2">
              <Disclosure
                tone="strong"
                defaultOpen={c.failed > 0 && data.checks.filter((x) => x.failed > 0).length <= 2}
                summary={
                  <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span>{v('checkName', c.name) ?? <Untranslated>{c.name}</Untranslated>}</span>
                    {c.failed > 0 ? (
                      <Badge state={c.errors > 0 ? 'fail' : 'warn'}>
                        {t('viewer.checksFailed', { n: count(c.failed, locale) })}
                      </Badge>
                    ) : (
                      <Badge state="pass">{t('viewer.checksAllPass', { n: count(c.total, locale) })}</Badge>
                    )}
                    <span className="tnum text-[length:var(--text-micro)] text-[var(--text-faint)]">
                      {t('quality.passRateOf', {
                        pct: percent0(rate, locale),
                        passed: count(c.passed, locale),
                        total: count(c.total, locale),
                      })}
                    </span>
                    {c.openReview > 0 && (
                      <span className="tnum text-[length:var(--text-micro)] text-[var(--text-faint)]">
                        {tn('quality.openReview', c.openReview, { n: count(c.openReview, locale) })}
                      </span>
                    )}
                  </span>
                }
              >
                {c.samples.length === 0 ? (
                  <p className="ps-4 text-[length:var(--text-tiny)] text-[var(--text-faint)]">
                    {t('viewer.noFailures')}
                  </p>
                ) : (
                  <div className="ps-4 pt-1">
                    {/* Ordered by the size of the discrepancy, so the worst
                        readings are the ones on screen. */}
                    <Table>
                      <thead>
                        <Tr>
                          <Th>{t('docs.period')}</Th>
                          <Th>{t('fact.label')}</Th>
                          <Th numeric>{t('quality.expected')}</Th>
                          <Th numeric>{t('quality.actual')}</Th>
                          <Th numeric>{t('quality.delta')}</Th>
                        </Tr>
                      </thead>
                      <tbody>
                        {c.samples.map((s) => {
                          const detail = (s.detail ?? {}) as Record<string, unknown>
                          return (
                            <Tr key={s.id} interactive>
                              <Td>
                                <Link
                                  href={`/c/${symbol}/d/${s.documentId}${s.pageNo ? `?p=${s.pageNo}` : ''}`}
                                  className="tnum whitespace-nowrap link-inline"
                                >
                                  {s.period?.toUpperCase()} {s.fiscalYear}
                                  {s.pageNo && ` · p${s.pageNo}`}
                                </Link>
                              </Td>
                              <Td className="max-w-[22rem]">
                                <span dir="auto" className="block truncate">
                                  {typeof detail.label === 'string' ? detail.label : '—'}
                                </span>
                              </Td>
                              <Td numeric className="text-[var(--text-muted)]">
                                {s.expected === null ? '—' : money(s.expected, locale)}
                              </Td>
                              <Td numeric className="text-[var(--text-muted)]">
                                {s.actual === null ? '—' : money(s.actual, locale)}
                              </Td>
                              <Td numeric className="font-medium text-[var(--fail)]">
                                {s.delta === null ? '—' : money(s.delta, locale)}
                              </Td>
                            </Tr>
                          )
                        })}
                      </tbody>
                    </Table>
                    {c.failed > c.samples.length && (
                      <p className="mt-1 text-[length:var(--text-micro)] text-[var(--text-faint)]">
                        {count(c.samples.length, locale)} {t('common.of')}{' '}
                        {count(c.failed, locale)} — {t('quality.byCheck').toLowerCase()}
                      </p>
                    )}
                  </div>
                )}
              </Disclosure>
            </Panel>
          )
        })}
      </div>
    </div>
  )
}
